if (!customElements.get('collection-ajax-pagination')) {
  class CollectionAjaxPagination extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.abortController = null;
      this.observer = null;
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.mode = this.dataset.paginationMode || 'load_more';
      this.sectionId = this.dataset.sectionId || '';
      this.nextUrl = this.dataset.nextUrl || '';
      this.grid = this.closest('.collection')?.querySelector('#product-grid');
      this.button = this.querySelector('[data-collection-load-more]');
      this.buttonLabel = this.querySelector('[data-collection-load-more-label]');
      this.spinner = this.querySelector('.collection-ajax-pagination__button .collection-ajax-pagination__spinner');
      this.sentinel = this.querySelector('[data-collection-load-more-sentinel]');
      this.indicator = this.querySelector('[data-collection-load-more-indicator]');
      this.status = this.querySelector('[data-collection-load-more-status]');

      if (!this.grid || !this.nextUrl) {
        this.remove();
        return;
      }

      if (this.mode === 'infinite_scroll' && this.sentinel && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection, {
          rootMargin: '500px 0px',
          threshold: 0,
        });
        this.observer.observe(this.sentinel);
        return;
      }

      if (this.button) {
        this.button.addEventListener('click', this.handleButtonClick);
      }
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleButtonClick);
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    handleButtonClick() {
      this.loadNextPage();
    }

    handleIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadNextPage();
      }
    }

    getRequestUrl() {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.setAttribute('aria-busy', String(isLoading));

      if (this.button) {
        this.button.disabled = isLoading;
        if (this.buttonLabel) {
          this.buttonLabel.textContent = isLoading
            ? this.dataset.loadingLabel || 'Loading...'
            : this.dataset.buttonLabel || 'Load more';
        }
      }

      if (this.spinner) this.spinner.hidden = !isLoading;
      if (this.indicator) this.indicator.hidden = !isLoading;
    }

    prepareProductItem(item) {
      item.classList.add('scroll-trigger--cancel');
      item.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
      return item;
    }

    async loadNextPage() {
      if (this.loading || !this.nextUrl || !this.grid) return;

      this.dataset.buttonLabel = this.buttonLabel?.textContent || 'Load more';
      this.dataset.loadingLabel =
        this.dataset.loadingLabel ||
        this.querySelector('[data-collection-load-more-indicator] span:last-child')?.textContent ||
        'Loading...';
      this.setLoading(true);
      if (this.status) this.status.textContent = this.dataset.loadingLabel;

      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(this.getRequestUrl(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Collection pagination request failed: ${response.status}`);
        }

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const nextGrid = responseDocument.querySelector('#product-grid');
        const nextPagination = responseDocument.querySelector(
          `collection-ajax-pagination[data-section-id="${this.sectionId}"]`
        );
        const nextItems = nextGrid ? Array.from(nextGrid.children) : [];

        if (!nextItems.length) {
          throw new Error('Collection pagination returned no product cards.');
        }

        const fragment = document.createDocumentFragment();
        nextItems.forEach((item) => fragment.appendChild(this.prepareProductItem(item)));
        this.grid.appendChild(fragment);

        this.nextUrl = nextPagination?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;

        window.DmWishlistController?.refresh?.();
        document.dispatchEvent(
          new CustomEvent('collection:products-appended', {
            detail: {
              sectionId: this.sectionId,
              itemCount: nextItems.length,
            },
          })
        );

        if (this.status) {
          this.status.textContent = `${nextItems.length} more products loaded.`;
        }

        if (!this.nextUrl) {
          this.observer?.disconnect();
          this.remove();
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        if (this.status) {
          this.status.classList.remove('visually-hidden');
          this.status.textContent = 'Products could not be loaded. Please try again.';
        }

        if (this.mode === 'infinite_scroll') {
          this.observer?.disconnect();
        }
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }
  }

  customElements.define('collection-ajax-pagination', CollectionAjaxPagination);
}
